import { Component, inject, computed, OnInit, signal, effect, Injector } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { TopbarComponent } from './shared/components/topbar/topbar.component';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';
import { ToastHostComponent } from './shared/components/toast/toast-host.component';
import { ApiService, UserStateService, AppStateService, UserService, GlobalsService, EditorStateService } from './core/services';
import { AppState } from './core/models';
import { SupportDialogComponent } from './features/support/support-dialog/support-dialog.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, TopbarComponent, ImpersonationBannerComponent, ToastHostComponent, TranslateModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private api = inject(ApiService);
  private userState = inject(UserStateService);
  readonly appState = inject(AppStateService);
  private userService = inject(UserService);
  private globals = inject(GlobalsService);
  private translate = inject(TranslateService);
  private editorState = inject(EditorStateService);
  private injector = inject(Injector);

  isLoggedIn = this.userState.isLoggedIn;
  isSupportSession = this.userState.isSupportSession;

  // Sync hide-prices class on body so it covers CDK overlay containers (dialogs)
  private hidePricesEffect = effect(() => {
    document.body.classList.toggle('hide-prices', this.appState.hidePrices());
  });

  // Global loading overlay for capture chain (persists across route navigation)
  captureChainLoading = computed(() => this.editorState.captureChainLoading());
  // Global loading overlay while editor lazy-loads
  editorOpening = computed(() => this.editorState.editorOpening());
  
  // Track the last home state for proper logo display
  lastHome = signal<AppState>(AppState.HOME_DREAMBOOKSPRO);
  
  showTopbar = computed(() => {
    // Editor focus mode hides every chrome around the canvas, this included
    if (this.editorState.focusMode()) return false;
    const state = this.appState.state();
    // Hide topbar for login, loading, and guest preview states
    const hiddenStates = [AppState.LOGIN, AppState.LOADING, AppState.GUEST_PREVIEW];
    return this.isLoggedIn() && !hiddenStates.includes(state);
  });

  ngOnInit(): void {
    console.log('[App] ngOnInit - checking session');

    // Initialize translation service
    this.initTranslations();

    // Check if this is a public guest preview route (no auth required)
    if (this.isGuestPreviewRoute()) {
      console.log('[App] Guest preview route detected, skipping auth check');
      this.appState.setState(AppState.GUEST_PREVIEW);
      return;
    }

    // Viva Smart Checkout return popup (/vivapayments/success|failure): a
    // relay-only page that posts the result to the opener and closes. It must
    // NOT bootstrap the session — running checkSession here calls /users/me,
    // and when the popup closes mid-request the aborted call hits the error
    // path that clears tokens from the SHARED localStorage, logging the main
    // window out too. (Observed: jwt + refreshToken deleted on Viva return.)
    if (this.isVivaReturnRoute()) {
      console.log('[App] Viva return route — relay only, skipping auth check');
      return;
    }

    // Public password-reset page (recovery + sub-user invite emails): render
    // without bootstrapping the session. An anonymous visitor must not be
    // bounced to /login, and a logged-in visitor must not be yanked to /home
    // by handleLoginSuccess's post-login navigation.
    if (this.isPasswordResetRoute()) {
      console.log('[App] Public reset-password route — skipping auth bootstrap');
      return;
    }

    // Capture deep link before auto-login potentially redirects
    this.storeInitialRedirect();

    // Check for saved session first
    this.userService.checkSession();

    // Once session check completes, redirect to login if not authenticated (one-time only).
    // Using a one-shot subscription instead of a reactive effect to avoid
    // re-firing on every isLoggedIn change during the login flow.
    toObservable(this.userState.sessionReady, { injector: this.injector }).pipe(
      filter(ready => ready),
      take(1)
    ).subscribe(() => {
      if (!this.isLoggedIn()) {
        console.log('[App] Not logged in, redirecting to login');
        this.router.navigate(this.appState.buildLocaleCommands(['login']));
      } else {
        console.log('[App] User is logged in, updating lastHome');
        this.updateLastHome();
      }
    });
  }

  /**
   * Check if current URL is a guest preview route (public, no auth required).
   *
   * Strips the locale segment first so locale-prefixed aliases like
   * `/pt_PT/share/:token` and `/pt_PT/album/preview/:token` are recognised the
   * same as the bare `/share/:token` form. Without this the app treats
   * locale-prefixed guest URLs as authed routes and either redirects the
   * visitor to /login (no session) or renders the photographer topbar over
   * the guest shell (with session).
   */
  private isGuestPreviewRoute(): boolean {
    const path = window.location.pathname || '';
    const pathLocale = this.getLocaleFromPath(path);
    const stripped = pathLocale ? path.replace(`/${pathLocale}`, '') || '/' : path;
    return stripped.includes('/album/preview/')
      || stripped.startsWith('/g/')
      || stripped.startsWith('/p/')
      || stripped.startsWith('/share/');
  }

  /** Viva return relay routes — top-level, no locale prefix. */
  private isVivaReturnRoute(): boolean {
    return (window.location.pathname || '').startsWith('/vivapayments/');
  }

  /** Public password-reset page (/:locale/reset-password?token=…). */
  private isPasswordResetRoute(): boolean {
    const path = window.location.pathname || '';
    const pathLocale = this.getLocaleFromPath(path);
    const stripped = pathLocale ? path.replace(`/${pathLocale}`, '') || '/' : path;
    return stripped.startsWith('/reset-password');
  }

  private initTranslations(): void {
    // Set default and fallback language
    this.translate.setDefaultLang('pt_PT');

    // Keep <html lang> telling the truth on every language switch — screen
    // readers pick their voice from it, spellcheck picks its dictionary.
    // Underscore locales → BCP 47 ("pt_PT" → "pt-PT"). Subscribed before the
    // first use() so the initial locale stamps too. Translation BLOCKING does
    // not rely on this: index.html carries translate="no" + the google meta.
    this.translate.onLangChange.subscribe(event => {
      document.documentElement.lang = (event.lang || 'pt_PT').replace('_', '-');
    });

    // Prefer locale from URL, then saved locale, then fallback
    const pathLocale = this.getLocaleFromPath(window.location.pathname || '');
    const savedLocale = pathLocale || this.appState.locale() || 'pt_PT';
    if (pathLocale && this.appState.locale() !== pathLocale) {
      this.appState.setLocale(pathLocale);
    }
    this.translate.use(savedLocale);
    
    console.log('[App] Translations initialized with locale:', savedLocale);
  }

  private storeInitialRedirect(): void {
    const path = window.location.pathname || '';
    const pathLocale = this.getLocaleFromPath(path);
    let normalizedPath = pathLocale ? path.replace(`/${pathLocale}`, '') : path;
    if (!normalizedPath) {
      normalizedPath = '/';
    } else if (!normalizedPath.startsWith('/')) {
      normalizedPath = `/${normalizedPath}`;
    }

    // reset-password is excluded: replaying a used token page after the
    // subsequent login would land the user on a dead form instead of home.
    if (!normalizedPath || normalizedPath === '/' || normalizedPath === '/login'
        || normalizedPath.startsWith('/reset-password')) {
      sessionStorage.removeItem('postLoginRedirect');
      return;
    }
    const search = window.location.search || '';
    sessionStorage.setItem('postLoginRedirect', `${path}${search}`);
  }

  private getLocaleFromPath(path: string): string | null {
    if (!path) {
      return null;
    }

    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      return null;
    }

    const candidate = segments[0];
    const supportedLocales = this.globals.languages.map(language => language.locale);
    return supportedLocales.includes(candidate) ? candidate : null;
  }

  private updateLastHome(): void {
    if (this.globals.hasDreambooksPro) {
      this.lastHome.set(AppState.HOME_DREAMBOOKSPRO);
    } else if (this.globals.hasOneLabPro) {
      this.lastHome.set(AppState.HOME_ONELABPRO);
    } else if (this.globals.hasLfmPro) {
      this.lastHome.set(AppState.HOME_LFMPRO);
    }
  }

  onNavigateHome(state: AppState): void {
    this.lastHome.set(state);
    this.appState.setState(state);
    this.router.navigate(this.appState.buildLocaleCommands(['home']));
  }

  onNavigateOrders(): void {
    // Navigate to orders page on the production tab (Encomendas = orders in production)
    this.router.navigate(this.appState.buildLocaleCommands(['orders']), { queryParams: { tab: 'production' } });
  }

  onNavigateDrafts(): void {
    // Navigate to orders page with drafts tab (index 0)
    this.router.navigate(this.appState.buildLocaleCommands(['orders']), { queryParams: { tab: 'drafts' } });
  }

  onNavigateCart(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['cart']));
  }

  onNavigateProfile(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['profile']));
  }

  onNavigateProjects(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['projects']));
  }

  onNavigateUploads(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['uploads']));
  }

  onNavigateGalleries(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['galleries']));
  }

  onNavigateAlbumDesign(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['album-design']));
  }

  onNavigateDesigner(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['designer']));
  }

  onNavigateSupport(): void {
    const dialogRef = this.dialog.open(SupportDialogComponent, {
      width: '440px',
      maxWidth: '95vw',
      panelClass: 'support-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'login') {
        console.log('[App] Support login completed, refreshing...');
        // Optionally refresh the current page or navigate home
        this.updateLastHome();
        this.router.navigate(this.appState.buildLocaleCommands(['home']));
      }
    });
  }

  onExitSupportSession(): void {
    console.log('[App] Exiting support session...');
    // UserService.exitSupportSession wraps the token/user swap, restores
    // userType on globals, AND re-fetches the admin's post-login data
    // (addresses, projects, drafts…) which were dropped on session exit.
    // Skipping that re-fetch leaves the cart's address list empty until
    // the next full login.
    const originalUser = this.userService.exitSupportSession();
    if (originalUser) {
      this.updateLastHome();
      // Navigate home to refresh everything
      this.router.navigate(this.appState.buildLocaleCommands(['home']));
    }
  }

  onLogout(): void {
    this.userService.logout();
    this.router.navigate(this.appState.buildLocaleCommands(['login']));
  }
}
